// Angular
import {
  Component,
  inject,
  Output,
  signal,
  EventEmitter,
  ElementRef,
  ViewChild,
  DestroyRef,
  Signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
// PrimeNG
import { RadioButtonModule } from 'primeng/radiobutton';
// Models
import { PaymentTypeOption } from './models/payment-type-option.interface';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { PaymentType } from './models/payment-type.type';
// Components
import { PurchaseOrderFormComponent } from './components/purchase-order-form/purchase-order-form.component';
import { InvoiceFormComponent } from './components/invoice-form/invoice-form.component';
import { DocumentTypeFormComponent } from './components/document-type-form/document-type-form.component';
import { PaymentMethodFormComponent } from './components/payment-method-form/payment-method-form.component';

// Services
import { SessionService } from '@core/services-v2/session/session.service';
// Directives
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { IShoppingCart } from '@core/models-v2/cart/shopping-cart.interface';
import { CartService } from '@core/services-v2/cart.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { ISession } from '@core/models-v2/auth/session.interface';
import { PaymentMethodService } from '@core/services-v2/payment-method.service';
import { CustomerService } from '@core/services-v2/customer.service';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { CartStorageService } from '@core/services-v2/cart/cart-storage/cart-storage.service';
import { PurchaseOrderForm } from './components/purchase-order-form/models/purchase-order-form.interface';
import { TermsOfServiceInputComponent } from '@shared/components-v3/inputs/terms-of-service-input/terms-of-service-input.component';
import { environment } from '@env/environment';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PaymentDocumentType } from '@core/enums/payment-document-type.enum';
import { InvoiceForms } from './components/invoice-form/models/invoice-forms.interface';
import { CheckoutStorageService } from '../../services/checkout-storage/checkout-storage.service';

import { PaypalComponent } from './paypal/paypal.component';
import { Input } from '@angular/core';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [RadioButtonModule];
const COMPONENTS = [
  PurchaseOrderFormComponent,
  InvoiceFormComponent,
  DocumentTypeFormComponent,
  PaymentMethodFormComponent,
  TermsOfServiceInputComponent,
];
const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

@Component({
  selector: 'app-payment-step',
  standalone: true,
  imports: [
    NG_MODULES,
    PRIME_MODULES,
    COMPONENTS,
    DIRECTIVES,
    PaypalComponent,
  ],
  templateUrl: './payment-step.component.html',
  host: { class: 'w-full mb-6 lg:mb-0' },
})
export class PaymentStepComponent {
  //#region Output events
  @Output() onChange: EventEmitter<PaymentType> = new EventEmitter();
  @Output() onFormSubmit = new EventEmitter<PaymentType>();
  @Input() totalCarro: number = 0;
  //#endregion

  //#region ViewChild elements
  @ViewChild('paymentTypeFormElement')
  paymentTypeFormElement!: ElementRef<HTMLFormElement>;
  @ViewChild('termsOfServiceFormElement')
  termsOfServiceFormElement!: ElementRef<HTMLFormElement>;
  //#endregion

  //#region ViewChild components
  @ViewChild('invoiceFormComponent')
  invoiceFormComponent!: InvoiceFormComponent;
  @ViewChild('purchaseOrderFormComponent')
  purchaseOrderFormComponent!: PurchaseOrderFormComponent;
  //#endregion

  //#region Forms
  paymentTypeForm!: FormGroup<ControlsOf<{ paymentType: PaymentType }>>;
  termsOfServiceForm!: FormGroup<ControlsOf<{ termsOfService: boolean }>>;
  //#endregion

  paymentDocumentType = signal<PaymentDocumentType>(
    PaymentDocumentType.RECEIPT
  );
  isLoadingPayments = signal<boolean>(true);

  payments: PaymentTypeOption[] = [];
  cartSession!: IShoppingCart;
  userSession!: ISession;
  customerBlocked: boolean = false;
  isTermsOfServiceVisible =
    environment.legalResourcesVisibility.termsOfServiceOnPayment;
  countryCode = environment.country;

  //#region Getters
  get paymentTypeField() {
    return this.paymentTypeForm.get('paymentType');
  }
  //#endregion

  //#region Dependencies
  private readonly sessionService = inject(SessionService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cartService = inject(CartService);
  private readonly cartApiService = inject(CartApiService);
  private readonly cartStorageService = inject(CartStorageService);
  private readonly paymentMethodService = inject(PaymentMethodService);
  private readonly customerService = inject(CustomerService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  //#endregion

  private isLargeScreen$: Observable<boolean> = this.breakpointObserver
    .observe(['(min-width: 992px)'])
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      map((result) => result.matches)
    );
  isLargeScreen: Signal<boolean> = toSignal(this.isLargeScreen$, {
    initialValue: false,
  });

  constructor() {
    const storedPaymentDocumentType =
      this.checkoutStorageService.getPaymentDocumentType();
    if (storedPaymentDocumentType) {
      this.paymentDocumentType.set(storedPaymentDocumentType);
    } else {
      this.checkoutStorageService.setPaymentDocumentType(
        PaymentDocumentType.RECEIPT
      );
    }
    this.buildForms();
  }

  ngOnInit(): void {
    this.cartSession = this.cartStorageService.get()!;
    this.userSession = this.sessionService.getSession()!;
    this.isTermsOfServiceVisible = this.termsOfServiceVisible();
    if (
      this.sessionService.isEnterprise() ||
      !this.sessionService.isLoggedIn()
    ) {
      this.paymentDocumentType.set(PaymentDocumentType.INVOICE);
      this.checkoutStorageService.setPaymentDocumentType(
        PaymentDocumentType.INVOICE
      );
    }
    this.updateObservationCart();
    this.cartService.load();
    this.setPaymentTypes();
  }

  termsOfServiceVisible(): boolean {
    if (this.isTermsOfServiceVisible && this.countryCode === 'pe') return true;
    if (
      this.isTermsOfServiceVisible &&
      this.countryCode === 'cl' &&
      !this.sessionService.isLoggedIn()
    )
      return true;
    return false;
  }

  private async updateObservationCart() {
    const recipient = this.checkoutStorageService.getRecipientAnother();
    if (!this.cartSession) return;
    const cartId = this.cartSession._id!.toString();
    const shoppingCart = await firstValueFrom(
      this.cartApiService.updateRecipient({
        cartId,
        recipient: recipient?.documentId
          ? {
              documentId: recipient.documentId,
              firstName: recipient.name ?? '',
              lastName: recipient.lastName ?? '',
              phone: recipient.phone ?? '',
            }
          : {},
      })
    );
    this.cartSession = shoppingCart;
  }

  setPaymentTypes(): void {
    if (this.countryCode === 'es') {
      this.isLoadingPayments.set(false);
      return;
    }

    this.isLoadingPayments.set(true);
    let documentId = this.userSession?.documentId ?? '';
    let username = this.userSession?.username ?? '';
    this.paymentMethodService.getPaymentMethodsNew({ username }).subscribe({
      next: (data) => {
        const ordenCompraCode = 'ordenCompra';
        const hasOC = data.some((r) => r.code === ordenCompraCode);
        if (hasOC) {
          this.customerService.getCustomerBlocked(documentId).subscribe({
            next: (resp) => {
              if (resp.isBlocked) {
                this.customerBlocked = true;
                data = data.filter((r) => r.code !== ordenCompraCode);
              }
              this.payments = data;
              this.isLoadingPayments.set(false);
            },
            error: (e) => {
              console.error(e);
              this.customerBlocked = true;
              data = data.filter((r) => r.code !== ordenCompraCode);
              this.payments = data;
              this.isLoadingPayments.set(false);
            },
          });
        } else {
          this.payments = data;
          this.isLoadingPayments.set(false);
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoadingPayments.set(false);
        // this.toastr.error('No se pudo cargar los métodos de pago');
      },
    });
  }

  private buildForms(): void {
    this.paymentTypeForm = this.fb.group({
      paymentType: new FormControl<PaymentType>('' as PaymentType, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
    if (this.isTermsOfServiceVisible && this.isLargeScreen()) {
      this.termsOfServiceForm = this.fb.group({
        termsOfService: [false],
      });
    }
  }

  /**
   * Handle the shipment type selection.
   * @param value
   */
  onSelectPaymentType(value: PaymentType): void {
    this.paymentTypeForm.setValue({ paymentType: value });
    this.onChange.emit(value);
  }

  /**
   * Cambia el tipo de documento.
   * @param type
   */
  onDocumentTypeChange(type: PaymentDocumentType): void {
    this.paymentDocumentType.set(type);
    this.checkoutStorageService.setPaymentDocumentType(type);
  }

  isValidInvoiceForm(): boolean {
    // Verificar que el componente existe y está inicializado
    if (
      !this.invoiceFormComponent ||
      this.paymentDocumentType() !== 'invoice'
    ) {
      return true; // o false, según tu lógica de negocio
    }
    return this.invoiceFormComponent.invoiceForm.valid;
  }

  /**
   * Dispara los eventos submit de los formularios y valida sus estados.
   * - Este método es llamado desde el componente padre.
   * @returns {boolean} Retorna true si todos los formularios son válidos, false en caso contrario.
   */
  submit(): {
    paymentType: PaymentType;
    purchaseOrder: PurchaseOrderForm | null;
    invoice: InvoiceForms | null;
  } | null {
    if (this.countryCode === 'es') {
      return {
        paymentType: 'paypal' as PaymentType,
        purchaseOrder: null,
        invoice: null,
      };
    }

    // Se valida factura
    let isValidInvoiceForm = true;
    let invoice: InvoiceForms | null = null;
    if (
      this.paymentDocumentType() === 'invoice' &&
      !this.sessionService.isEnterprise()
    ) {
      invoice = this.invoiceFormComponent.submit();
      isValidInvoiceForm = !!invoice;

      // FIXME: correct validation
      /*isValidInvoiceForm = this.sessionService.isEnterprise()
        ? true
        : !!invoice;*/
    }

    // Se valida método de pago
    this.paymentTypeFormElement.nativeElement.dispatchEvent(
      new Event('submit', { cancelable: true })
    );

    // Se valida orden de compra
    let isValidPurchaseOrderForm = true;
    let purchaseOrder = null;
    if (this.paymentTypeField?.value === 'ordenCompra') {
      purchaseOrder = this.purchaseOrderFormComponent.submit();
      isValidPurchaseOrderForm = purchaseOrder !== null;
    }

    // Se valida términos y condiciones
    let isValidTermsOfServiceForm = true;
    if (this.isTermsOfServiceVisible && this.isLargeScreen()) {
      this.termsOfServiceFormElement.nativeElement.dispatchEvent(
        new Event('submit', { cancelable: true })
      );
      // FIXME: termsOfServiceForm undefined?
      isValidTermsOfServiceForm = this.termsOfServiceForm.valid;
    }

    // Validación final
    const isValid =
      isValidInvoiceForm &&
      isValidPurchaseOrderForm &&
      isValidTermsOfServiceForm &&
      this.paymentTypeForm.valid;
    return isValid
      ? {
          paymentType: this.paymentTypeForm.getRawValue().paymentType,
          purchaseOrder,
          invoice,
        }
      : null;
  }

  /**
   * Dispara el evento submit del formulario de método de pago y valida su estado.
   */
  onPaymentMethodSubmit(): void {
    if (this.paymentTypeForm.valid) {
      this.onFormSubmit.emit(this.paymentTypeForm.value.paymentType);
    } else {
      this.paymentTypeForm.markAllAsTouched();
    }
  }

  onSubmitTermsOfServiceForm(): void {
    if (!this.termsOfServiceForm.valid) {
      this.termsOfServiceForm.markAllAsTouched();
    }
  }
}
